import React from "react";
import { Fade } from "react-awesome-reveal";
import { MdChevronRight } from "react-icons/md";

import "../../styling.css";
import BackgroundCover from "../../../components/backgroundCover/BackgroundCover";
import TitleWithDescription from "../../../components/TitleWithDescription/TitleWithDescription";
import { isMobile } from "../../../utils/appUtils";
import Title from "../../../components/Title/Title";
import Collection from "../../../components/Collection/Collection";
import { useLanguage } from "../../../context/LanguageContext";
import { getSnakeTranslations, SnakeTranslations } from "../../../translations/snakeTemplate";
import { useEditMode } from "../../../context/EditModeContext";
import EditableText from "../../../components/EditableText/EditableText";
import { usePageContent } from "../../../hooks/usePageContent";

function SnakeCollection() {
    const { language } = useLanguage();
    const { isEditMode } = useEditMode();
    const { content, loading } = usePageContent<SnakeTranslations>('snake-collection');

    // Use translations as fallback content
    const t = content || getSnakeTranslations(language);

    // Show loading state if needed
    if (loading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="animate-spin h-12 w-12 border-2 border-gray-400 rounded-full border-t-transparent"></div>
            </div>
        );
    }

    const renderContent = () => {
        const collectionItemsForGrid = t.collectionItems;

        return (
            <>
                {/* Snake Symbol Section */}
                <div className="py-4 md:py-8">
                    <TitleWithDescription
                        title={{
                            firstLine: isEditMode ? (
                                <EditableText
                                    contentKey="snake-collection.symbolTitle"
                                    defaultValue={t.symbolTitle}
                                />
                            ) : t.symbolTitle,
                            secondLine: isEditMode ? (
                                <EditableText
                                    contentKey="snake-collection.symbolSubtitle"
                                    defaultValue={t.symbolSubtitle}
                                />
                            ) : t.symbolSubtitle
                        }}
                        description={isEditMode ? (
                            <EditableText
                                contentKey="snake-collection.symbolDescription"
                                defaultValue={t.symbolDescription}
                                multiline={true}
                            />
                        ) : t.symbolDescription}
                        layout="row"
                        className="px-4 md:px-8 lg:px-24"
                    />
                </div>

                {/* Animal of Paradise Section */}
                <TitleWithDescription
                    title={{
                        firstLine: isEditMode ? (
                            <EditableText
                                contentKey="snake-collection.paradiseTitle"
                                defaultValue={t.paradiseTitle}
                            />
                        ) : t.paradiseTitle,
                        secondLine: isEditMode ? (
                            <EditableText
                                contentKey="snake-collection.paradiseSubtitle"
                                defaultValue={t.paradiseSubtitle}
                            />
                        ) : t.paradiseSubtitle
                    }}
                    description={isEditMode ? (
                        <EditableText
                            contentKey="snake-collection.paradiseDescription"
                            defaultValue={t.paradiseDescription}
                            multiline={true}
                        />
                    ) : t.paradiseDescription}
                    layout="column"
                    image={{
                        src: "/images/jewellery_snake_grid_1_ring1.png",
                        alt: "Snake jewelry piece",
                        position: "left",
                        className: "w-full md:w-auto md:max-w-[90%] h-auto"
                    }}
                    className="bg-gradient-to-br from-[#E1D8D3] via-[#E0DDDD] to-[#E0DEDF] px-4 md:px-8 lg:px-24"
                />

                {/* The Collection intro */}
                <TitleWithDescription
                    title={{
                        firstLine: isEditMode ? (
                            <EditableText
                                contentKey="snake-collection.moreTitle"
                                defaultValue={t.moreTitle}
                            />
                        ) : t.moreTitle,
                        secondLine: isEditMode ? (
                            <EditableText
                                contentKey="snake-collection.moreSubtitle"
                                defaultValue={t.moreSubtitle}
                            />
                        ) : t.moreSubtitle
                    }}
                    description={isEditMode ? (
                        <EditableText
                            contentKey="snake-collection.moreDescription"
                            defaultValue={t.moreDescription}
                            multiline={true}
                        />
                    ) : t.moreDescription}
                    layout="row"
                    className="px-4 md:px-8 lg:px-24"
                />

                {/* The Collection */}
                <div className="pb-8 md:pb-16">
                    <Collection
                        items={collectionItemsForGrid}
                        collectionId="snake"
                        buildItemLink={(item) => `/jewellery/collections/snake/${item.id}`}
                    />
                </div>
            </>
        );
    };

    return (
        <div className="min-h-screen bg-white">
            <BackgroundCover
                backgroundHeight={isMobile() ? 400 : undefined}
                imagePosition="center"
                backgroundImage="/images/jewellery_snake_background.jpg"
                isWatchPage={true}
            >
                <div className="absolute top-1/2 left-[10%] md:left-[100px] -translate-y-1/2 z-10">
                    <Title
                        firstLine={isEditMode ? (
                            <EditableText
                                contentKey="snake-collection.mainTitle"
                                defaultValue={t.mainTitle}
                            />
                        ) : t.mainTitle}
                        secondLine={isEditMode ? (
                            <EditableText
                                contentKey="snake-collection.mainSubtitle"
                                defaultValue={t.mainSubtitle}
                            />
                        ) : t.mainSubtitle}
                        className="hidden md:block"
                        animate={true}
                    />
                </div>
            </BackgroundCover>
            <div className="w-full">
                {renderContent()}
            </div>
        </div>
    );
}

export default SnakeCollection;
